/**
 * AxLabs Experiments - Simple Pendulum
 *
 * A classic physics experiment demonstrating oscillatory motion.
 * Uses the full nonlinear equation of motion (NOT the small angle approximation).
 *
 * Governing equation: d²θ/dt² = -(g/L)sin(θ) - b(dθ/dt)
 *
 * This is a foundational experiment that teaches:
 * - Period depends on length, not amplitude (for small angles)
 * - Period deviation at large angles
 * - Energy conservation (or loss with damping)
 * - Phase space analysis
 */
import { BaseSimulationEngine, DIMENSIONS, verletStep, vec2, } from '@axlabs/simulation-core';
// ============================================================================
// PARAMETERS
// ============================================================================
const PARAMETERS = [
    {
        id: 'length',
        name: 'Pendulum Length',
        symbol: 'L',
        unit: 'm',
        dimension: DIMENSIONS.LENGTH,
        min: 0.1,
        max: 10,
        default: 1,
        step: 0.1,
        description: 'Length of the pendulum from pivot to center of mass',
    },
    {
        id: 'initialAngle',
        name: 'Initial Angle',
        symbol: '\\theta_0',
        unit: '°',
        dimension: DIMENSIONS.DIMENSIONLESS, // angles are dimensionless in SI
        min: 0,
        max: 179,
        default: 30,
        step: 1,
        description: 'Starting angle from vertical (degrees)',
    },
    {
        id: 'gravity',
        name: 'Gravitational Acceleration',
        symbol: 'g',
        unit: 'm/s²',
        dimension: DIMENSIONS.ACCELERATION,
        min: 0.1,
        max: 25,
        default: 9.81,
        step: 0.01,
        description: 'Local gravitational field strength',
    },
    {
        id: 'damping',
        name: 'Damping Coefficient',
        symbol: 'b',
        unit: '1/s',
        dimension: DIMENSIONS.FREQUENCY,
        min: 0,
        max: 2,
        default: 0,
        step: 0.01,
        description: 'Energy dissipation rate (0 = no damping)',
    },
    {
        id: 'mass',
        name: 'Bob Mass',
        symbol: 'm',
        unit: 'kg',
        dimension: DIMENSIONS.MASS,
        min: 0.01,
        max: 100,
        default: 1,
        step: 0.1,
        description: 'Mass of the pendulum bob (affects energy, not motion for point mass)',
    },
];
// ============================================================================
// EQUATIONS
// ============================================================================
const EQUATIONS = [
    {
        id: 'motion',
        name: 'Equation of Motion',
        latex: '\\frac{d^2\\theta}{dt^2} = -\\frac{g}{L}\\sin(\\theta) - b\\frac{d\\theta}{dt}',
        variables: ['theta', 'omega', 'gravity', 'length', 'damping'],
        description: 'Full nonlinear pendulum equation with optional damping',
    },
    {
        id: 'period_small',
        name: 'Period (Small Angle)',
        latex: 'T \\approx 2\\pi\\sqrt{\\frac{L}{g}}',
        variables: ['length', 'gravity'],
        description: 'Approximate period for small oscillations (θ < 15°)',
        condition: (params) => Math.abs(params['initialAngle'] ?? 0) < 15,
    },
    {
        id: 'period_large',
        name: 'Period (Large Angle)',
        latex: 'T = 2\\pi\\sqrt{\\frac{L}{g}}\\left(1 + \\frac{1}{16}\\theta_0^2 + ...\\right)',
        variables: ['length', 'gravity', 'initialAngle'],
        description: 'Period correction for large amplitudes',
        condition: (params) => Math.abs(params['initialAngle'] ?? 0) >= 15,
    },
    {
        id: 'kinetic_energy',
        name: 'Kinetic Energy',
        latex: 'KE = \\frac{1}{2}mL^2\\omega^2',
        variables: ['mass', 'length', 'omega'],
        description: 'Rotational kinetic energy of the pendulum',
    },
    {
        id: 'potential_energy',
        name: 'Potential Energy',
        latex: 'PE = mgL(1 - \\cos\\theta)',
        variables: ['mass', 'gravity', 'length', 'theta'],
        description: 'Gravitational potential energy (zero at lowest point)',
    },
    {
        id: 'total_energy',
        name: 'Total Energy',
        latex: 'E = \\frac{1}{2}mL^2\\omega^2 + mgL(1 - \\cos\\theta)',
        variables: ['mass', 'gravity', 'length', 'theta', 'omega'],
        description: 'Total mechanical energy (conserved without damping)',
    },
];
// ============================================================================
// FAILURE MODES
// ============================================================================
const FAILURE_MODES = [
    {
        id: 'over_top',
        name: 'Over the Top',
        description: 'Pendulum has enough energy to go over the pivot point',
        check: (state) => Math.abs(state.variables['theta'] ?? 0) > Math.PI,
        severity: 'warning',
        educationalMessage: 'The pendulum has enough energy to swing completely over the top. ' +
            'This transitions from oscillatory to rotational motion. ' +
            'In a real pendulum, this would require very high initial energy.',
    },
    {
        id: 'energy_divergence',
        name: 'Energy Divergence',
        description: 'Total energy is increasing without external input',
        check: (state) => state.flags.energyConservationViolation,
        severity: 'error',
        educationalMessage: 'Energy is not being conserved. This indicates numerical instability. ' +
            'Try reducing the timestep or initial angle.',
    },
];
// ============================================================================
// EXPERIMENT CONFIG
// ============================================================================
export const SIMPLE_PENDULUM_CONFIG = {
    id: 'simple-pendulum',
    slug: 'simple-pendulum',
    name: 'Simple Pendulum',
    domain: 'physics',
    description: 'Explore oscillatory motion with a simple pendulum',
    longDescription: `
The simple pendulum is a fundamental physics experiment demonstrating periodic motion.
A mass (bob) is suspended from a pivot by a massless, inextensible string.

Key concepts:
- Period is independent of mass (for point mass approximation)
- Period depends primarily on length and gravitational acceleration
- Small angle approximation: period is independent of amplitude
- Large angles: period increases with amplitude
- Energy oscillates between kinetic and potential forms
- Damping causes gradual decay of oscillations

This simulation uses the FULL nonlinear equation, not the small angle approximation,
so you can observe the deviation in period at large angles.
  `.trim(),
    version: 1,
    conceptTags: ['oscillation', 'periodic-motion', 'energy-conservation', 'phase-space'],
    difficulty: 3,
    prerequisites: ['kinematics', 'energy'],
    parameters: PARAMETERS,
    equations: EQUATIONS,
    initialConditions: [
        { variableId: 'theta', type: 'parameter', parameterId: 'initialAngle' },
        { variableId: 'omega', type: 'fixed', value: 0 },
    ],
    failureModes: FAILURE_MODES,
    recommendedDt: 1 / 120,
    maxDt: 1 / 30,
};
// ============================================================================
// SIMULATION ENGINE
// ============================================================================
export class SimplePendulumSimulation extends BaseSimulationEngine {
    config = SIMPLE_PENDULUM_CONFIG;
    constructor() {
        super({ theta: 0, omega: 0, x: 0, y: 0 }, { type: 'verlet', dt: 1 / 120, maxSubsteps: 10 });
    }
    getInitialState(params) {
        const initialAngleDeg = params['initialAngle'] ?? 30;
        const initialAngleRad = initialAngleDeg * (Math.PI / 180);
        const length = params['length'] ?? 1;
        return {
            theta: initialAngleRad,
            omega: 0,
            x: length * Math.sin(initialAngleRad),
            y: -length * Math.cos(initialAngleRad), // y is negative (down)
        };
    }
    computeDerivatives(state, params) {
        const g = params['gravity'] ?? 9.81;
        const L = params['length'] ?? 1;
        const b = params['damping'] ?? 0;
        // Angular acceleration: α = -(g/L)sin(θ) - b*ω
        const alpha = -(g / L) * Math.sin(state.theta) - b * state.omega;
        return {
            theta: state.omega,
            omega: alpha,
        };
    }
    integrate(dt) {
        const g = this.parameters['gravity'] ?? 9.81;
        const L = this.parameters['length'] ?? 1;
        const b = this.parameters['damping'] ?? 0;
        // Use Verlet integration for better energy conservation
        const getAcceleration = (pos, vel) => {
            // pos.x = theta, pos.y = 0 (unused)
            // vel.x = omega, vel.y = 0 (unused)
            const theta = pos.x;
            const omega = vel.x;
            const alpha = -(g / L) * Math.sin(theta) - b * omega;
            return vec2(alpha, 0);
        };
        const currentPos = vec2(this.state.theta, 0);
        const currentVel = vec2(this.state.omega, 0);
        const currentAcc = getAcceleration(currentPos, currentVel);
        const result = verletStep(currentPos, currentVel, currentAcc, getAcceleration, dt);
        this.state.theta = result.position.x;
        this.state.omega = result.velocity.x;
        // Update Cartesian coordinates
        this.state.x = L * Math.sin(this.state.theta);
        this.state.y = -L * Math.cos(this.state.theta);
    }
    computeEnergy(state, params) {
        const m = params['mass'] ?? 1;
        const g = params['gravity'] ?? 9.81;
        const L = params['length'] ?? 1;
        // Rotational kinetic energy: KE = (1/2) * I * ω² = (1/2) * m * L² * ω²
        const kinetic = 0.5 * m * L * L * state.omega * state.omega;
        // Gravitational potential energy: PE = m * g * h = m * g * L * (1 - cos(θ))
        const potential = m * g * L * (1 - Math.cos(state.theta));
        return {
            kinetic,
            potential,
            thermal: 0, // Damping converts to thermal, but we don't track it
            total: kinetic + potential,
        };
    }
    getPositionForStabilityCheck() {
        return this.state.theta;
    }
    getBounds() {
        // Allow full rotation (-2π to 2π) before flagging
        return {
            theta: { min: -2 * Math.PI, max: 2 * Math.PI },
        };
    }
    /**
     * Calculate theoretical period using elliptic integral approximation
     */
    getTheoreticalPeriod() {
        const g = this.parameters['gravity'] ?? 9.81;
        const L = this.parameters['length'] ?? 1;
        const theta0Deg = this.parameters['initialAngle'] ?? 30;
        const theta0 = theta0Deg * (Math.PI / 180);
        // Small angle period
        const T0 = 2 * Math.PI * Math.sqrt(L / g);
        // Series correction for large angles
        // T ≈ T0 * (1 + θ0²/16 + 11θ0⁴/3072 + ...)
        const correction = 1 +
            (theta0 * theta0) / 16 +
            (11 * theta0 * theta0 * theta0 * theta0) / 3072;
        return T0 * correction;
    }
}
/**
 * Factory function to create a new pendulum simulation
 */
export function createSimplePendulum() {
    return new SimplePendulumSimulation();
}
//# sourceMappingURL=simple-pendulum.js.map